import React, { useState, useEffect } from 'react';
import { OCCASION_PAGES, getOccasionBySlug } from '../data/occasionPages';
import { trackEvent } from '../utils/telemetry';

const OccasionLandingPage = ({ slug, onSelectOccasion, onNavigateHome }) => {
  const [activeTab, setActiveTab] = useState('concept'); // 'concept' | 'menu' | 'decor' | 'timeline'
  const [copiedHex, setCopiedHex] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const occasion = getOccasionBySlug(slug);

  useEffect(() => {
    if (occasion) {
      document.title = `${occasion.title} — CraftMyParty`;
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', occasion.metaDescription);
      }
      trackEvent('occasion_page_viewed', {
        slug: occasion.slug,
        occasion: occasion.occasion,
        theme_name: occasion.themeName
      });
    } else {
      document.title = 'Party Themes & Aesthetic Blueprints — CraftMyParty';
      trackEvent('occasion_directory_viewed');
    }
  }, [occasion, slug]);

  const handleCopyHex = (hex) => {
    navigator.clipboard?.writeText(hex);
    setCopiedHex(hex);
    setTimeout(() => setCopiedHex(null), 1800);
  };

  const handleShareLink = () => {
    navigator.clipboard?.writeText(window.location.href);
    setCopiedLink(true);
    trackEvent('occasion_cta_clicked', {
      slug: occasion?.slug,
      action: 'share_link'
    });
    setTimeout(() => setCopiedLink(false), 2200);
  };

  const handleLaunchCustomize = () => {
    trackEvent('occasion_cta_clicked', {
      slug: occasion?.slug,
      action: 'customize_party'
    });
    if (typeof onSelectOccasion === 'function') {
      onSelectOccasion(occasion);
    }
  };

  // If slug is not found or user is browsing /occasions root
  if (!occasion) {
    return (
      <div className="min-h-screen w-full max-w-full overflow-x-clip bg-gradient-to-b from-stone-950 via-zinc-900 to-black text-white selection:bg-purple-500 selection:text-white">
        {/* Navigation Bar */}
        <header className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5 sm:px-6">
            <button
              type="button"
              onClick={onNavigateHome}
              className="group flex items-center gap-2.5 text-left focus:outline-none"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-purple-600 via-pink-500 to-amber-400 font-black text-white shadow-lg shadow-purple-500/25">
                C
              </div>
              <div>
                <span className="text-base font-extrabold tracking-tight text-white group-hover:text-purple-300 transition">
                  CraftMyParty
                </span>
                <span className="ml-2 text-xs font-semibold text-purple-400 uppercase tracking-widest hidden sm:inline">
                  Occasions
                </span>
              </div>
            </button>

            <button
              type="button"
              onClick={onNavigateHome}
              className="rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold text-white hover:bg-white/20 transition backdrop-blur-md"
            >
              Back to Home
            </button>
          </div>
        </header>

        {/* Directory Hero */}
        <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block rounded-full border border-purple-500/30 bg-purple-500/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-purple-300 mb-3">
              Curated Celebration Blueprints
            </span>
            <h1 className="text-3xl font-black tracking-tight sm:text-5xl text-white">
              Trending Party Themes & Aesthetic Guides
            </h1>
            <p className="mt-4 text-base text-zinc-400 sm:text-lg">
              Explore complete party concepts with color palettes, signature cocktail menus, decor shopping checklists, and hour-by-hour itineraries.
            </p>
          </div>

          {/* Occasion Cards Grid */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {OCCASION_PAGES.map((item) => (
              <div
                key={item.slug}
                className="group relative flex flex-col justify-between overflow-hidden rounded-3xl border border-white/10 bg-zinc-900/60 p-6 backdrop-blur-xl transition hover:border-purple-500/50 hover:bg-zinc-900/90 shadow-lg"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="rounded-full bg-purple-500/20 px-2.5 py-0.5 text-[11px] font-bold text-purple-300">
                      {item.badge}
                    </span>
                    <span className="text-xs text-zinc-400 font-medium">
                      {item.occasion}
                    </span>
                  </div>

                  <h2 className="text-xl font-bold tracking-tight text-white group-hover:text-purple-300 transition">
                    {item.themeName}
                  </h2>
                  <p className="mt-2 text-xs leading-relaxed text-zinc-400 line-clamp-3">
                    {item.description}
                  </p>

                  {/* Palette preview */}
                  <div className="mt-4 flex items-center gap-1.5">
                    {item.palette.map((c) => (
                      <span
                        key={c.hex}
                        className="h-5 w-5 rounded-full border border-white/20 shadow-sm"
                        style={{ backgroundColor: c.hex }}
                        title={`${c.name} (${c.hex})`}
                      />
                    ))}
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                  <span className="text-xs font-semibold text-zinc-400">
                    Est: {item.estimatedCostRange}
                  </span>
                  <a
                    href={`/occasions/${item.slug}`}
                    className="inline-flex items-center gap-1 text-xs font-bold text-purple-400 hover:text-purple-300 transition"
                  >
                    View Blueprint →
                  </a>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // Render Full Occasion Blueprint Landing Page
  return (
    <div className="min-h-screen w-full max-w-full overflow-x-clip bg-gradient-to-b from-stone-950 via-zinc-900 to-black text-white selection:bg-purple-500 selection:text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3.5 sm:px-6">
          <button
            type="button"
            onClick={onNavigateHome}
            className="group flex items-center gap-2.5 text-left focus:outline-none"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-purple-600 via-pink-500 to-amber-400 font-black text-white shadow-lg shadow-purple-500/25">
              C
            </div>
            <div>
              <span className="text-base font-extrabold tracking-tight text-white group-hover:text-purple-300 transition">
                CraftMyParty
              </span>
            </div>
          </button>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href="/occasions"
              className="rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-semibold text-zinc-300 hover:bg-white/20 transition hidden sm:inline-block"
            >
              All Themes
            </a>
            <button
              type="button"
              onClick={handleLaunchCustomize}
              className="rounded-full bg-gradient-to-r from-purple-600 via-pink-600 to-amber-500 px-4 py-1.5 text-xs font-bold text-white shadow-lg shadow-purple-500/25 hover:opacity-95 transition"
            >
              Customize Free
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-xs font-semibold text-zinc-400 mb-6">
          <button onClick={onNavigateHome} className="hover:text-white transition">
            Home
          </button>
          <span>/</span>
          <a href="/occasions" className="hover:text-white transition">
            Occasions
          </a>
          <span>/</span>
          <span className="text-purple-400 truncate">{occasion.themeName}</span>
        </nav>

        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-zinc-950/90 p-6 sm:p-10 backdrop-blur-2xl shadow-2xl">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="rounded-full bg-purple-500/20 px-3 py-1 text-xs font-bold text-purple-300 border border-purple-500/30">
              {occasion.badge}
            </span>
            <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-zinc-300">
              {occasion.occasion}
            </span>
            <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-zinc-300">
              {occasion.archetype}
            </span>
          </div>

          <h1 className="text-2xl font-black tracking-tight sm:text-4xl lg:text-5xl text-white">
            {occasion.headline}
          </h1>

          <p className="mt-4 text-sm leading-relaxed text-zinc-300 sm:text-base max-w-3xl">
            {occasion.description}
          </p>

          {/* Palette Swatch Bar */}
          <div className="mt-8 rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-5 backdrop-blur-md">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">
                Curated Color Palette
              </span>
              <span className="text-[11px] text-zinc-500">
                Click any swatch to copy HEX
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {occasion.palette.map((color) => {
                const isCopied = copiedHex === color.hex;
                return (
                  <button
                    key={color.hex}
                    type="button"
                    onClick={() => handleCopyHex(color.hex)}
                    className="group relative flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/5 p-2 transition hover:border-purple-500 hover:bg-white/10 text-left"
                  >
                    <span
                      className="h-8 w-8 flex-shrink-0 rounded-lg shadow-inner border border-white/20"
                      style={{ backgroundColor: color.hex }}
                    />
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-white truncate group-hover:text-purple-300 transition">
                        {isCopied ? 'Copied!' : color.name}
                      </div>
                      <div className="text-[11px] font-mono text-zinc-400">
                        {color.hex}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Metrics & CTA Bar */}
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-white/10 pt-6">
            <div className="flex items-center gap-6">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                  Target Budget
                </div>
                <div className="text-base font-extrabold text-white">
                  {occasion.estimatedCostRange}
                </div>
              </div>
              <div className="h-8 w-px bg-white/10" />
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                  Ideal Guests
                </div>
                <div className="text-base font-extrabold text-white">
                  {occasion.defaultGuests} guests
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleShareLink}
                className="rounded-xl border border-white/20 bg-white/5 px-4 py-2.5 text-xs font-bold text-white hover:bg-white/10 transition"
              >
                {copiedLink ? '✓ Link Copied' : 'Share Theme'}
              </button>
              <button
                type="button"
                onClick={handleLaunchCustomize}
                className="rounded-xl bg-gradient-to-r from-purple-600 via-pink-600 to-amber-500 px-6 py-2.5 text-xs font-extrabold text-white shadow-lg shadow-purple-500/25 hover:opacity-95 transition"
              >
                Customize This Party →
              </button>
            </div>
          </div>
        </div>

        {/* Interactive Blueprint Tabs */}
        <div className="mt-10">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-white/10">
            {[
              { id: 'concept', label: 'Concept Highlights', icon: '✨' },
              { id: 'menu', label: 'Sips & Food Pairings', icon: '🍸' },
              { id: 'decor', label: 'Decor & Shopping List', icon: '🛍️' },
              { id: 'timeline', label: 'Party Timeline', icon: '⏱️' }
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 rounded-xl px-4 py-2 text-xs sm:text-sm font-bold transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content Panels */}
          <div className="mt-6">
            {activeTab === 'concept' && (
              <div className="rounded-3xl border border-white/10 bg-zinc-900/60 p-6 sm:p-8 backdrop-blur-xl">
                <h3 className="text-lg font-bold text-white mb-4">
                  Theme Vibe & Atmosphere Highlights
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {occasion.highlights.map((h, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 rounded-2xl border border-white/5 bg-white/5 p-4"
                    >
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-purple-500/20 text-xs font-bold text-purple-300">
                        {i + 1}
                      </span>
                      <p className="text-sm text-zinc-300 leading-relaxed">{h}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'menu' && (
              <div className="rounded-3xl border border-white/10 bg-zinc-900/60 p-6 sm:p-8 backdrop-blur-xl space-y-8">
                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <span>🍸</span> Signature Sips & Cocktails
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {occasion.signatureSips.map((sip, idx) => (
                      <div
                        key={idx}
                        className="rounded-2xl border border-white/5 bg-white/5 p-4"
                      >
                        <h4 className="text-sm font-bold text-white">{sip.name}</h4>
                        <p className="mt-1.5 text-xs text-zinc-400 leading-relaxed">
                          {sip.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <span>🍽️</span> Gourmet Food Pairings
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {occasion.foodPairings.map((food, idx) => (
                      <div
                        key={idx}
                        className="rounded-2xl border border-white/5 bg-white/5 p-4"
                      >
                        <h4 className="text-sm font-bold text-white">{food.name}</h4>
                        <p className="mt-1.5 text-xs text-zinc-400 leading-relaxed">
                          {food.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'decor' && (
              <div className="rounded-3xl border border-white/10 bg-zinc-900/60 p-6 sm:p-8 backdrop-blur-xl">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <span>🛍️</span> Recommended Decor & Supplies
                  </h3>
                  <span className="text-xs text-zinc-400 font-medium">
                    Available on Amazon & Party Specialty Retailers
                  </span>
                </div>
                <div className="divide-y divide-white/10">
                  {occasion.decorChecklist.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-4 py-3.5"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-purple-400 font-bold">✓</span>
                        <span className="text-sm font-semibold text-zinc-200">
                          {item.name}
                        </span>
                      </div>
                      <span className="text-xs font-mono font-semibold text-zinc-400 whitespace-nowrap">
                        {item.est}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'timeline' && (
              <div className="rounded-3xl border border-white/10 bg-zinc-900/60 p-6 sm:p-8 backdrop-blur-xl">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <span>⏱️</span> Sample Party Flow & Itinerary
                </h3>
                <div className="relative pl-6 sm:pl-8 space-y-6 before:absolute before:bottom-2 before:left-2 before:top-2 before:w-0.5 before:bg-purple-500/30">
                  {occasion.sampleTimeline.map((slot, idx) => (
                    <div key={idx} className="relative">
                      <span className="absolute -left-[23px] sm:-left-[31px] top-1 h-3.5 w-3.5 rounded-full border-2 border-purple-500 bg-black" />
                      <div className="text-xs font-mono font-bold text-purple-400">
                        {slot.time}
                      </div>
                      <h4 className="text-sm font-bold text-white mt-0.5">
                        {slot.title}
                      </h4>
                      <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                        {slot.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Viral Bottom Funnel Banner */}
        <div className="mt-14 overflow-hidden rounded-3xl border border-purple-500/30 bg-gradient-to-r from-purple-950/60 via-zinc-900 to-pink-950/50 p-6 sm:p-10 text-center backdrop-blur-xl shadow-2xl">
          <h3 className="text-xl font-black text-white sm:text-3xl">
            Want this exact party customized for your event?
          </h3>
          <p className="mt-2 text-sm text-zinc-300 max-w-xl mx-auto">
            Input your date, guest count, and budget to generate a full customized shopping cart, interactive moodboard, and digital invites in 2 minutes.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={handleLaunchCustomize}
              className="w-full sm:w-auto rounded-full bg-white px-8 py-3 text-sm font-extrabold text-black shadow-lg hover:bg-zinc-200 transition"
            >
              Customize with AI — 100% Free
            </button>
            <a
              href="/occasions"
              className="w-full sm:w-auto rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition"
            >
              Browse All 6 Themes
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OccasionLandingPage;
